import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { CopyLinkButton } from '@/components/assessment-engine/CopyLinkButton';
import {
  AE_BTN_PRIMARY,
  AE_INPUT,
  AE_LABEL,
  AE_MUTED,
  AE_TEXTAREA,
} from '@/components/assessment-engine/layout/aeTheme';

interface CampaignInviteListFieldsProps {
  /** Create wizard only — optional emails applied after campaign is created. */
  pendingEmails?: string;
  onPendingEmailsChange?: (text: string) => void;
}

/** Optional invite list on the new-campaign form (hub uses Invitations & responses tab). */
export const CampaignInviteListFields: React.FC<CampaignInviteListFieldsProps> = ({
  pendingEmails = '',
  onPendingEmailsChange,
}) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-4">
      <div>
        <label className={AE_LABEL}>{t('assessmentEngine.hub.participants')}</label>
        <p className={`${AE_MUTED} text-sm mb-2`}>{t('assessmentEngine.wizard.inviteEmailsCreateHint')}</p>
        <textarea
          className={AE_TEXTAREA}
          rows={3}
          placeholder="one@example.com, two@example.com"
          value={pendingEmails}
          onChange={(e) => onPendingEmailsChange?.(e.target.value)}
        />
      </div>
    </div>
  );
};

interface CampaignPublicLinkFieldProps {
  publicUrl: string;
}

export const CampaignPublicLinkField: React.FC<CampaignPublicLinkFieldProps> = ({ publicUrl }) => {
  const { t } = useLanguage();
  return (
    <div>
      <label className={AE_LABEL}>{t('assessmentEngine.hub.publicLink')}</label>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          readOnly
          className={`${AE_INPUT} flex-1 min-w-0 text-sm bg-[var(--color-hover-bg)]`}
          value={publicUrl}
        />
        <CopyLinkButton url={publicUrl} />
      </div>
    </div>
  );
};

/** Toolbar: public link + open add-invites modal. */
export const CampaignInvitationsToolbar: React.FC<{
  distributionMode: 'public' | 'invite_only';
  publicUrl: string;
  canAddInvites: boolean;
  onAddInvites: () => void;
}> = ({ distributionMode, publicUrl, canAddInvites, onAddInvites }) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-2">
      {distributionMode === 'public' && <CampaignPublicLinkField publicUrl={publicUrl} />}
      {canAddInvites && (
        <div>
          <button type="button" className={AE_BTN_PRIMARY} onClick={onAddInvites}>
            {t('assessmentEngine.hub.addInvites')}
          </button>
        </div>
      )}
    </div>
  );
};
